import { useCallback, useEffect, useState } from 'react';

import { readAudit, auditActions, type AuditEntry } from '../../lib/admin';
import { ApiError } from '../../lib/apiClient';
import { formatDateTime, shortId } from '../../lib/format';
import './AdminPages.css';

const PAGE = 50;

/**
 * Audit log viewer. Read-only, and it stays that way.
 *
 * audit_log is insert-only by design — the engine exposes no update or delete
 * path anywhere — so this screen offers no edit affordance at all. Filters map
 * straight to the engine's query parameters rather than fetching everything and
 * narrowing in the browser.
 */
export function AdminAudit() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [actions, setActions] = useState<{ action: string; count: number }[]>([]);
  const [filters, setFilters] = useState({ action: '', entityType: '', entityId: '', since: '' });
  const [applied, setApplied] = useState({ action: '', entityType: '', entityId: '', since: '' });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    auditActions().then(setActions).catch(() => setActions([]));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    readAudit({ ...applied, limit: PAGE, offset })
      .then((r) => { setEntries(r.entries); setTotal(r.total); })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Could not read the audit log.');
        setEntries([]);
      })
      .finally(() => setLoading(false));
  }, [applied, offset]);

  useEffect(() => { load(); }, [load]);

  const apply = (e: React.FormEvent) => {
    e.preventDefault();
    setOffset(0);
    setApplied(filters);
  };

  const page = Math.floor(offset / PAGE) + 1;
  const pages = Math.max(1, Math.ceil(total / PAGE));

  return (
    <div className="adm-page">
      <header className="adm-header">
        <div>
          <h1 className="adm-title">Audit log</h1>
          <p className="adm-lede">
            Permanent record of every financial and access event. Insert-only, nothing
            here can be edited or removed.
          </p>
        </div>
        <div className="adm-stat">
          <span className="adm-stat__value mono">{total.toLocaleString('en-US')}</span>
          <span className="adm-stat__label">entries matching</span>
        </div>
      </header>

      <form className="adm-filters" onSubmit={apply}>
        <select
          className="adm-select"
          value={filters.action}
          onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))}
        >
          <option value="">All actions</option>
          {actions.map((a) => (
            <option key={a.action} value={a.action}>
              {a.action} ({a.count})
            </option>
          ))}
        </select>
        <input
          className="field__input adm-search"
          type="text"
          placeholder="Entity type (event, order…)"
          value={filters.entityType}
          onChange={(e) => setFilters((f) => ({ ...f, entityType: e.target.value }))}
        />
        <input
          className="field__input adm-search mono"
          type="text"
          placeholder="Entity id"
          value={filters.entityId}
          onChange={(e) => setFilters((f) => ({ ...f, entityId: e.target.value }))}
        />
        <input
          className="field__input"
          type="date"
          value={filters.since}
          onChange={(e) => setFilters((f) => ({ ...f, since: e.target.value }))}
        />
        <button type="submit" className="button button--secondary">Filter</button>
        {Object.values(applied).some(Boolean) && (
          <button
            type="button"
            className="button button--ghost"
            onClick={() => {
              const empty = { action: '', entityType: '', entityId: '', since: '' };
              setFilters(empty); setApplied(empty); setOffset(0);
            }}
          >
            Clear
          </button>
        )}
      </form>

      {error && <p className="adm-status adm-error" role="alert">{error}</p>}
      {loading && <p className="adm-status">Loading…</p>}
      {!loading && entries.length === 0 && (
        <p className="adm-status adm-muted">No entries match.</p>
      )}

      {!loading && entries.length > 0 && (
        <>
          <div className="adm-table-wrap">
            <table className="adm-table adm-table--dense">
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col">Action</th>
                  <th scope="col">Entity</th>
                  <th scope="col">Actor</th>
                  <th scope="col">IP</th>
                  <th scope="col">Details</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id}>
                    <td className="mono adm-nowrap">{formatDateTime(e.createdAt)}</td>
                    <td><span className="adm-pill adm-pill--action">{e.action}</span></td>
                    <td>
                      {e.entityType ? (
                        <span className="adm-entity">
                          <span className="adm-entity__type">{e.entityType}</span>
                          {e.entityId && (
                            <span className="mono adm-muted" title={e.entityId}>
                              {shortId(e.entityId)}
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="adm-muted"> | </span>
                      )}
                    </td>
                    <td>
                      {e.actor ? (
                        <span className="adm-entity">
                          <span className="mono">{e.actor.email}</span>
                          <span className="adm-muted">{e.actor.role}</span>
                        </span>
                      ) : (
                        <span className="adm-muted">system</span>
                      )}
                    </td>
                    <td className="mono adm-muted">{e.ipAddress ?? ' | '}</td>
                    <td>
                      {e.details ? (
                        <button
                          type="button"
                          className="adm-link"
                          onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                        >
                          {expanded === e.id ? 'Hide' : 'Show'}
                        </button>
                      ) : (
                        <span className="adm-muted"> | </span>
                      )}
                      {expanded === e.id && e.details && (
                        <pre className="adm-json mono">{JSON.stringify(e.details, null, 2)}</pre>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="adm-pager">
            <button
              type="button"
              className="button button--ghost adm-btn-sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE))}
            >
              Previous
            </button>
            <span className="adm-muted">
              Page <span className="mono">{page}</span> of <span className="mono">{pages}</span>
            </span>
            <button
              type="button"
              className="button button--ghost adm-btn-sm"
              disabled={offset + PAGE >= total}
              onClick={() => setOffset(offset + PAGE)}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}
